import React from "react";
import { Box, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { labToDisplayRgb } from "../helper/colorConversion";

/*
 * "Sample point details" as shown on the exploration analyse page (Design &
 * performance tab): the header facts in a two-column grid, the design
 * parameters as outlined chips, the colour measurement as three bordered
 * swatch cards (CIELAB / CIEXYZ / RGB) and the width measurement as chips.
 * One presentation for a sample wherever it is shown. Every card's swatch is
 * the sample's CIELAB colour; the cards differ only in the values listed.
 */

export const formatTo3 = (value) => {
    // Number(null) and Number("") are 0: a missing value must not read as 0.000.
    if (value === null || value === undefined || value === "") return "n/a";
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue.toFixed(3) : "n/a";
};

export const formatParameterLabel = (value) =>
    String(value || "")
        .replaceAll("_", " ")
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

function SwatchCard({ title, css, lines, tooltip }) {
    return (
        <Tooltip title={tooltip || title}>
            <Box sx={{ width: 108, p: 0.75, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                <Box
                    sx={{
                        width: "100%", height: 34, borderRadius: 1, border: "1px solid", borderColor: "divider",
                        bgcolor: css || "transparent",
                        backgroundImage: css ? "none" : "repeating-linear-gradient(45deg, #e5e7eb 0, #e5e7eb 4px, #f8fafc 4px, #f8fafc 8px)",
                    }}
                />
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>{title}</Typography>
                {lines.map((line) => (
                    <Typography key={line} variant="caption" sx={{ display: "block" }}>{line}</Typography>
                ))}
            </Box>
        </Tooltip>
    );
}

/**
 * @param facts        [{label, value}] for the header grid (Sample ID, Comes from, ...)
 * @param designSpace  {name: value}
 * @param lab, xyz     [L, a, b] / [X, Y, Z] (measured colour)
 * @param cameraRgb    [r, g, b] camera reading (0..1 or 0..255)
 * @param widthMm, widthPx  width measurement
 * @param colorTitle   heading of the colour cards (default "Color measurements")
 */
export default function SamplePointDetails({
    facts = [], designSpace = {}, lab, xyz, cameraRgb, widthMm, widthPx,
    colorTitle = "Color measurements",
}) {
    const design = Object.entries(designSpace || {}).sort(([a], [b]) => a.localeCompare(b));
    const swatch = labToDisplayRgb(lab);
    return (
        <Stack spacing={1.5}>
            {facts.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                    {facts.map((fact) => (
                        <Typography key={fact.label} variant="body2" sx={{ wordBreak: "break-all" }}>
                            <strong>{fact.label}:</strong> {fact.value ?? "-"}
                        </Typography>
                    ))}
                </Box>
            )}

            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Design parameters</Typography>
            {design.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {design.map(([name, value]) => (
                        <Chip key={name} size="small" variant="outlined" label={`${formatParameterLabel(name)}: ${formatTo3(value)}`} />
                    ))}
                </Stack>
            ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No design parameter values found for this sample.
                </Typography>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{colorTitle}</Typography>
            {lab || xyz || cameraRgb ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {lab && (
                        <SwatchCard title="CIELAB" css={swatch}
                                    lines={[`L* ${formatTo3(lab[0])}`, `a* ${formatTo3(lab[1])}`, `b* ${formatTo3(lab[2])}`]} />
                    )}
                    {xyz && (
                        <SwatchCard title="CIEXYZ" css={swatch}
                                    lines={[`X ${formatTo3(xyz[0])}`, `Y ${formatTo3(xyz[1])}`, `Z ${formatTo3(xyz[2])}`]} />
                    )}
                    {cameraRgb && (
                        <SwatchCard title="RGB" css={swatch}
                                    lines={[`R ${formatTo3(cameraRgb[0])}`, `G ${formatTo3(cameraRgb[1])}`, `B ${formatTo3(cameraRgb[2])}`]} />
                    )}
                </Stack>
            ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>No colour measurement for this sample.</Typography>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Width measurements</Typography>
            {widthMm != null || widthPx != null ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {widthMm != null && <Chip size="small" variant="outlined" label={`${formatTo3(widthMm)} mm`} sx={{ cursor: "default" }} />}
                    {widthPx != null && <Chip size="small" variant="outlined" label={`${formatTo3(widthPx)} px`} sx={{ cursor: "default" }} />}
                </Stack>
            ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>No width measurement for this sample.</Typography>
            )}
        </Stack>
    );
}
